import React, { useState, useEffect, useRef } from 'react';
import { ModelPicker, PromptLink } from './modelInfo';
import { backdropClose } from '../backdrop';

// Answer-variants batch panel: the third pipeline. For every selected point the
// server asks the model for extra ACCEPTED spellings of the answer — synonyms,
// other languages (incl. the quest's location language), word forms, translit,
// conservative typos — dedupes them (case/space-insensitive, matching how the
// app compares a guess) and appends the new ones to the point's answer list.
// Auto-applied: no human-approval gate. Points run in parallel server-side.
// Same chrome as the other batches: scope modal, SSE progress, stop/clear.

const EXPANDED_TAG = '✅ Answers expanded';

const hasTask = (l) => !!(l.task || '').trim();
const acceptedAnswers = (l) => {
  const av = Array.isArray(l.answer_variations) ? l.answer_variations.filter((s) => typeof s === 'string' && s.trim()) : [];
  if (av.length) return av;
  const legacy = typeof l.answer === 'string' ? l.answer.trim() : '';
  return legacy ? [legacy] : [];
};
// A point can be expanded only if it has a task AND at least one answer.
const isExpandable = (l) => hasTask(l) && acceptedAnswers(l).length > 0;
const isExpanded = (l) => (l.tags || []).includes(EXPANDED_TAG);

async function papi(path, body) {
  const res = await fetch(`/api${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  let data = null;
  try { data = await res.json(); } catch (e) { /* empty body */ }
  if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);
  return data;
}

function fmtEta(ms) {
  if (ms == null) return '…';
  if (ms <= 0) return '0s';
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
}

const STATUS_CHIP = {
  queued: { bg: '#9aa0a6', label: 'queued' },
  running: { bg: '#0b8043', label: 'running' },
  done: { bg: '#188038', label: '✓ expanded' },
  skipped: { bg: '#9aa0a6', label: 'no new' },
  error: { bg: '#c5221f', label: 'error' },
  stopped: { bg: '#5f6368', label: 'stopped' },
  interrupted: { bg: '#5f6368', label: 'interrupted' },
};

const UNFINISHED = ['queued', 'running', 'interrupted', 'error'];

function PointRow({ bp }) {
  const chip = STATUS_CHIP[bp.status] || STATUS_CHIP.queued;
  const isRunning = bp.status === 'running';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '3px 6px', borderRadius: 4,
      background: isRunning ? 'rgba(11,128,67,0.10)' : 'transparent', fontSize: 12,
    }}>
      <span style={{
        background: chip.bg, color: '#fff', borderRadius: 10, padding: '1px 8px',
        fontSize: 10, textTransform: 'uppercase', whiteSpace: 'nowrap',
      }}>{chip.label}</span>
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bp.name}</span>
      {bp.status === 'done' && bp.added != null && (
        <span style={{ color: '#188038', whiteSpace: 'nowrap' }}>+{bp.added}</span>
      )}
      {bp.status === 'error' && bp.errors && bp.errors.length > 0 && (
        <span title={bp.errors.join('; ')} style={{ color: '#c5221f', whiteSpace: 'nowrap', cursor: 'help' }}>⚠</span>
      )}
    </div>
  );
}

const AnswerBatchPanel = ({ questId, runId, locations, pointsByUuid, initialBatch, embedded, onStatus }) => {
  const [batch, setBatch] = useState(initialBatch || null);
  const [active, setActive] = useState(!!(initialBatch && initialBatch.status === 'running'));
  const [showScope, setShowScope] = useState(false);
  const [selected, setSelected] = useState({}); // uuid -> bool
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (initialBatch && !batch && !clearedRef.current) {
      setBatch(initialBatch);
      if (initialBatch.status === 'running') setActive(true);
    }
  }, [initialBatch, batch]);

  const points = (locations || []).filter((l) => l.uuid && pointsByUuid && pointsByUuid[l.uuid]);
  // Default scope: everything expandable that this pipeline hasn't touched yet.
  const notExpandedUuids = points.filter((l) => isExpandable(l) && !isExpanded(l)).map((l) => l.uuid);
  const allExpandableUuids = points.filter(isExpandable).map((l) => l.uuid);

  const selectedUuids = points.filter((l) => selected[l.uuid]).map((l) => l.uuid);
  const selectedCount = selectedUuids.length;
  const sameSet = (arr) => arr.length === selectedCount && arr.every((u) => selected[u]);
  const activePreset = sameSet(notExpandedUuids) ? 'notexpanded'
    : sameSet(allExpandableUuids) ? 'all' : 'custom';
  const setPreset = (uuids) => setSelected(Object.fromEntries(uuids.map((u) => [u, true])));

  useEffect(() => {
    if (!active || !runId) return undefined;
    const es = new EventSource(`/api/pipeline/runs/${runId}/events`);
    es.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch (err) { return; }
      if (msg.type === 'answer_variants_batch') {
        setBatch(msg.batch);
      } else if (msg.type === 'answer_variants_batch_done') {
        setBatch(msg.batch);
        setActive(false);
      }
      // Written variants land in the editor live via the server's per-field
      // location_field_updated events — no quest refetch needed here.
    };
    es.onerror = () => { /* auto-reconnects; ignore */ };
    return () => es.close();
  }, [active, runId]);

  const openScope = () => {
    setError(null);
    setPreset(notExpandedUuids);
    setShowScope(true);
  };

  const start = async (body) => {
    setError(null); setBusy(true);
    try {
      const r = await papi(`/pipeline/quest/${questId}/answer-variants-batch`, body);
      clearedRef.current = false;
      setBatch({ status: 'running', total: r.total, done_count: 0, expanded_count: 0, added_total: 0, error_count: 0, points: [], eta_ms: null });
      setActive(true);
      setShowScope(false);
      setCollapsed(false);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const startBatch = () => {
    if (!selectedUuids.length) { setError('Select at least one point'); return; }
    start({ uuids: selectedUuids });
  };

  const stopBatch = async () => {
    setError(null);
    try { await papi(`/pipeline/quest/${questId}/answer-variants-batch/stop`, {}); } catch (e) { setError(e.message); }
  };

  const runUnfinished = () => {
    const uuids = (batch.points || []).filter((bp) => UNFINISHED.includes(bp.status)).map((bp) => bp.uuid);
    if (!uuids.length) return;
    start({ uuids });
  };

  const clearBatch = async () => {
    if (!window.confirm('Delete this answer-variants batch record? Written answer variants are kept.')) return;
    setError(null); setBusy(true);
    try {
      await papi(`/pipeline/quest/${questId}/answer-variants-batch/clear`, {});
      clearedRef.current = true;
      setBatch(null);
      setActive(false);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const running = active && batch && batch.status === 'running';
  const done = batch && batch.done_count != null ? batch.done_count : 0;
  const total = batch && batch.total != null ? batch.total : 0;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const unfinishedCount = batch ? (batch.points || []).filter((bp) => UNFINISHED.includes(bp.status)).length : 0;

  // Report status to the tab shell (badge + running dot) when embedded.
  useEffect(() => {
    if (onStatus) onStatus({ running, done, total, status: batch ? batch.status : null, hasBatch: !!batch });
  }, [running, done, total, batch, onStatus]);

  const effCollapsed = embedded ? false : collapsed;
  const box = embedded
    ? { padding: '4px 2px 2px' }
    : {
        border: '1px solid var(--border,#ddd)', borderRadius: 8, padding: '10px 12px',
        margin: '8px 8px 0', background: 'rgba(11,128,67,0.04)',
      };
  const primaryBtn = { background: '#0b8043', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 4, cursor: 'pointer', fontWeight: 600 };
  const stopBtn = { background: '#c5221f', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 4, cursor: 'pointer' };

  return (
    <div style={box}>
      <div
        onClick={embedded ? undefined : () => setCollapsed((c) => !c)}
        title={embedded ? undefined : (collapsed ? 'Expand' : 'Collapse')}
        style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', cursor: embedded ? 'default' : 'pointer' }}
      >
        {!embedded && <span style={{ fontWeight: 700, color: '#0b8043' }}>✅ Answers</span>}
        {/* The header toggles collapse — the picker must not trigger that. */}
        {!embedded && <span onClick={(e) => e.stopPropagation()} role="presentation"><ModelPicker k="answers" /></span>}
        {!embedded && collapsed && batch && (
          <span style={{ fontSize: 12, color: '#5f6368' }}>
            {batch.done_count || 0}/{batch.total || 0}
            {!running && batch.status ? ` · ${batch.status}` : ''}
          </span>
        )}
        {!running && (
          <button style={primaryBtn} disabled={busy || !points.length} onClick={(e) => { e.stopPropagation(); openScope(); }}>
            Expand answers…{notExpandedUuids.length ? ` (${notExpandedUuids.length} new)` : ''}
          </button>
        )}
        {running && <button style={stopBtn} onClick={(e) => { e.stopPropagation(); stopBatch(); }}>■ Stop (running points finish)</button>}
        {!running && batch && unfinishedCount > 0 && (
          <button onClick={(e) => { e.stopPropagation(); runUnfinished(); }} disabled={busy} style={{ padding: '6px 12px', borderRadius: 4 }}>
            ↻ Run unfinished ({unfinishedCount})
          </button>
        )}
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          {running && <span style={{ fontSize: 12, color: '#5f6368' }}>ETA {fmtEta(batch.eta_ms)}</span>}
          {!running && batch && (
            <button
              className="remove-location-button"
              onClick={(e) => { e.stopPropagation(); clearBatch(); }}
              disabled={busy}
              title="Delete this answer-variants batch record — written answer variants are kept"
              aria-label="Delete answer-variants batch record"
            >×</button>
          )}
          {!embedded && <span style={{ color: '#0b8043', fontSize: 13 }}>{collapsed ? '▸' : '▾'}</span>}
        </span>
      </div>

      {error && <div style={{ color: '#c5221f', fontSize: 12, marginTop: 6 }}>{error}</div>}

      {!effCollapsed && batch && (batch.points || []).length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 6 }}>
            <div style={{ flex: 1, height: 8, background: 'rgba(0,0,0,0.08)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: running ? '#0b8043' : '#188038', transition: 'width .3s' }} />
            </div>
            <span style={{ whiteSpace: 'nowrap', color: '#5f6368' }}>
              {done} / {total}
              {batch.added_total ? ` · +${batch.added_total} variants` : ''}
              {batch.error_count ? ` · ${batch.error_count} failed` : ''}
              {!running && batch.status ? ` · ${batch.status}` : ''}
            </span>
          </div>
          <div style={{ maxHeight: 220, overflow: 'auto', border: '1px solid var(--border,#eee)', borderRadius: 6, padding: 4 }}>
            {(batch.points || []).map((bp) => (
              <PointRow key={bp.uuid} bp={bp} />
            ))}
          </div>
        </div>
      )}

      {showScope && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} {...backdropClose(() => setShowScope(false))}>
          <div style={{ background: 'var(--bg,#fff)', borderRadius: 10, padding: 20, width: 540, maxWidth: '92vw' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              Expand answers <ModelPicker k="answers" /> <PromptLink k="answers" />
            </h3>
            <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Quick select:</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
              {[
                { key: 'notexpanded', label: `Not expanded yet (${notExpandedUuids.length})`, uuids: notExpandedUuids },
                { key: 'all', label: `All with a task+answer (${allExpandableUuids.length})`, uuids: allExpandableUuids },
              ].map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPreset(p.uuids)}
                  style={{
                    padding: '4px 10px', borderRadius: 14, cursor: 'pointer', fontSize: 12,
                    border: activePreset === p.key ? '2px solid #0b8043' : '1px solid var(--border,#ccc)',
                    background: activePreset === p.key ? 'rgba(11,128,67,0.10)' : 'transparent',
                    fontWeight: activePreset === p.key ? 600 : 400,
                  }}
                >{p.label}</button>
              ))}
              <button
                onClick={() => setSelected({})}
                style={{ padding: '4px 10px', borderRadius: 14, cursor: 'pointer', fontSize: 12, border: '1px solid var(--border,#ccc)', background: 'transparent', marginLeft: 'auto' }}
              >Clear</button>
            </div>

            {/* Explicit list of exactly what will run. */}
            <div style={{ maxHeight: 260, overflow: 'auto', border: '1px solid var(--border,#eee)', borderRadius: 6, padding: 6 }}>
              {points.map((l) => {
                const expandable = isExpandable(l);
                const expanded = isExpanded(l);
                const count = acceptedAnswers(l).length;
                return (
                  <label key={l.uuid} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '3px 2px', cursor: expandable ? 'pointer' : 'default', opacity: expandable ? 1 : 0.5 }}>
                    <input type="checkbox" disabled={!expandable} checked={!!selected[l.uuid]} onChange={(e) => setSelected((s) => ({ ...s, [l.uuid]: e.target.checked }))} />
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</span>
                    {expanded && (
                      <span style={{ fontSize: 10, color: '#188038', whiteSpace: 'nowrap' }} title={`Already expanded by this pipeline once («${EXPANDED_TAG}»)`}>✅ expanded</span>
                    )}
                    {!expandable
                      ? <span style={{ fontSize: 10, opacity: 0.6 }}>{hasTask(l) ? 'no answer' : 'no task'}</span>
                      : <span style={{ fontSize: 10, color: '#5f6368', whiteSpace: 'nowrap' }} title="Current number of accepted spellings">{count} now</span>}
                  </label>
                );
              })}
            </div>

            <div style={{ fontSize: 12, marginTop: 8 }}>
              <strong>{selectedCount}</strong> point(s) will be expanded
              {activePreset !== 'custom' ? '' : ' (custom)'}
            </div>
            <div style={{ fontSize: 12, color: '#0b8043', background: 'rgba(11,128,67,0.08)', borderRadius: 6, padding: '6px 8px', marginTop: 6 }}>
              Per point: the model returns extra accepted spellings of the answer — synonyms, other languages (incl. the quest's location language), word forms, transliterations, conservative typos — and the new ones are appended to the point's answer list automatically. Existing answers are never removed. Points run in parallel.
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowScope(false)}>Cancel</button>
              <button style={primaryBtn} disabled={busy || selectedCount === 0} onClick={startBatch}>Start ({selectedCount})</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnswerBatchPanel;
